// src/admin/pages/PropertiesPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, Filter, Star } from 'lucide-react';
import { formatImageUrl } from '../../services/api';
import { authService } from '../../services/auth';

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      // Pedido autenticado propio del admin, para ver también las propiedades ocultas
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/properties`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      setProperties(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta propiedad?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/properties/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) throw new Error('Error al eliminar');

      setProperties(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      alert('No se pudo eliminar la propiedad');
    }
  };

  const handleToggleFeatured = async (property) => {
    const newFeatured = !property.featured;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/properties/${property.id}/featured`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ featured: newFeatured })
      });

      if (!response.ok) throw new Error('Error al actualizar destacado');

      setProperties(prev => prev.map(p => (p.id === property.id ? { ...p, featured: newFeatured } : p)));
    } catch (error) {
      alert('No se pudo actualizar el estado de destacado');
    }
  };

  const handleToggleVisibility = async (property) => {
    const newPublished = property.published === false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/properties/${property.id}/visibility`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ published: newPublished })
      });

      if (!response.ok) throw new Error('Error al actualizar visibilidad');

      setProperties(prev => prev.map(p => (p.id === property.id ? { ...p, published: newPublished } : p)));
    } catch (error) {
      alert('No se pudo actualizar la visibilidad de la propiedad');
    }
  };

  const filteredProperties = properties.filter(property =>
    property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Propiedades</h1>
          <p className="text-gray-600">Gestiona todas las propiedades</p>
        </div>
        <Link
          to="/admin/properties/new"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg flex items-center transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Propiedad
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar propiedades por título o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <button className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            <Filter className="w-5 h-5 mr-2" />
            Filtros
          </button>
        </div>
      </div>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <div key={property.id} className={`bg-white rounded-xl shadow overflow-hidden ${property.published === false ? 'opacity-60' : ''}`}>
            {/* Property Image */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={formatImageUrl(property.image || (property.images && property.images[0]))}
                alt={property.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                {property.featured && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500 text-white flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" />
                    Destacada
                  </span>
                )}
                {property.published === false && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-white">
                    Oculta
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  property.category === 'terreno' ? 'bg-green-500' :
                  property.category === 'casa' ? 'bg-blue-500' :
                  property.category === 'departamento' ? 'bg-purple-500' :
                  'bg-yellow-500'
                } text-white`}>
                  {property.category}
                </span>
              </div>
            </div>

            {/* Property Info */}
            <div className="p-5">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg text-gray-800 truncate">
                  {property.title}
                </h3>
                <span className="text-lg font-bold text-blue-600">
                  {property.price}
                </span>
              </div>
              
              <p className="text-gray-600 mb-4 flex items-center">
                <span className="mr-2">📍</span>
                {property.location}
              </p>
              
              <p className="text-gray-700 text-sm line-clamp-2 mb-4">
                {property.description}
              </p>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex space-x-2">
                  <Link
                    to={`/admin/properties/edit/${property.id}`}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={() => handleDelete(property.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleToggleFeatured(property)}
                    className={`p-2 rounded-lg transition-colors ${
                      property.featured
                        ? 'text-amber-500 hover:bg-amber-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={property.featured ? 'Destacada - clic para quitar' : 'No destacada - clic para destacar'}
                  >
                    <Star className={`w-5 h-5 ${property.featured ? 'fill-amber-500' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleToggleVisibility(property)}
                    className={`p-2 rounded-lg transition-colors ${
                      property.published === false
                        ? 'text-gray-400 hover:bg-gray-100'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={property.published === false ? 'Oculta - clic para publicar' : 'Publicada - clic para ocultar'}
                  >
                    {property.published === false ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredProperties.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl shadow">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No se encontraron propiedades
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {searchTerm 
              ? `No hay propiedades que coincidan con "${searchTerm}"`
              : 'No hay propiedades registradas todavía'}
          </p>
          <Link
            to="/admin/properties/new"
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Crear primera propiedad
          </Link>
        </div>
      )}

      {/* Pagination */}
      {filteredProperties.length > 0 && (
        <div className="mt-8 flex justify-between items-center">
          <p className="text-gray-600">
            Mostrando {filteredProperties.length} de {properties.length} propiedades
          </p>
          <div className="flex space-x-2">
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Anterior
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              1
            </button>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              2
            </button>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;